/**
 * MicrosoftCallback — Vague 113 Wave 1k
 *
 * Handles the redirect back from Microsoft after `/auth/microsoft` was
 * registered as the redirect_uri. Reads `?code=...&state=...`, POSTs to
 * /api/auth/microsoft to exchange code for an OFManager session, then
 * navigates to the role-specific destination returned by the backend.
 *
 * REMINDER: DO NOT HARDCODE THE URL OR REDIRECT URI HERE. The redirect URI
 * is computed at runtime as `window.location.origin + '/auth/microsoft'`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import api, { setToken } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import OrgPicker from '../components/OrgPicker';

export default function MicrosoftCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  const [orgChoice, setOrgChoice] = useState(null);
  // Wave 1u9 — React.StrictMode mounts effects twice in dev. The OAuth state
  // row is single-use, so the second invocation would fail with "État OAuth
  // invalide ou expiré". Guard against re-entry with a ref.
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');
    const oauthErrorDescription = params.get('error_description');

    if (oauthError) {
      setError(`Microsoft a refusé la demande : ${oauthErrorDescription || oauthError}`);
      return;
    }
    if (!code) {
      setError('Code OAuth manquant — relancez la connexion depuis /login.');
      return;
    }

    // CSRF — verify state matches the nonce we stored before redirecting.
    const expectedState = sessionStorage.getItem('ms_oauth_state');
    sessionStorage.removeItem('ms_oauth_state');
    if (state && expectedState && state !== expectedState) {
      setError("Signature d'état invalide (CSRF). Relancez la connexion.");
      return;
    }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
    // Wave 1k.1 — same redirect URI as the mail flow (already registered in Azure).
    const redirectUri = `${window.location.origin}/api/integrations/microsoft/callback`;

    // Wave 1u3 — `Network Error` reported as intermittent on production. The
    // Microsoft auth POST does a heavy round-trip (Azure token exchange + Graph
    // /me + DB writes). On cold-start the container may take 5-15s to wake up
    // and Cloudflare can drop the underlying TCP connection. We retry up to
    // 2 times with back-off when axios reports `Network Error` / no response.
    const postWithRetry = async (body, attempt = 0) => {
      try {
        return await api.post('/auth/microsoft', body, { timeout: 30000 });
      } catch (e) {
        const isNetwork = !e?.response && (e?.code === 'ECONNABORTED' || /network/i.test(e?.message || ''));
        if (isNetwork && attempt < 2) {
          await new Promise((res) => setTimeout(res, 1200 * (attempt + 1)));
          return postWithRetry(body, attempt + 1);
        }
        throw e;
      }
    };

    (async () => {
      try {
        const linkMode = sessionStorage.getItem('ms_oauth_link_mode') === '1';
        sessionStorage.removeItem('ms_oauth_link_mode');

        const body = { code, redirect_uri: redirectUri };
        if (state) body.state = state;
        if (linkMode) body.link_mode = true;

        const { data } = await postWithRetry(body);

        if (data?.linked) {
          // LINK flow — keep existing session, refresh user, go back to Profil.
          setUser?.(data.user || null);
          const after = sessionStorage.getItem('ms_oauth_after');
          sessionStorage.removeItem('ms_oauth_after');
          navigate(after || '/app/profil?tab=security', { replace: true });
          return;
        }
        if (data?.requires_org_choice && data?.challenge) {
          // Multi-org — let the user pick the organization to enter.
          setOrgChoice(data);
          return;
        }
        if (data?.session_token) {
          setToken(data.session_token);
          setUser?.(data.user || null);
          const after = sessionStorage.getItem('ms_oauth_after');
          sessionStorage.removeItem('ms_oauth_after');
          navigate(after || data.redirect_path || '/app', { replace: true });
        } else {
          setError('Réponse inattendue du serveur — pas de session_token reçu.');
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || 'Connexion impossible';
        setError(detail);
      }
    })();
  }, [params, navigate, setUser]);

  if (orgChoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-[420px]">
          <OrgPicker choice={orgChoice} onBack={() => navigate('/login', { replace: true })} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="max-w-[420px] text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#FEF2F2] flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-[#EF4444]" />
          </div>
          <h1 className="mt-4 text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Connexion Microsoft impossible
          </h1>
          <p className="mt-2 text-[13.5px] text-[#6B6C85]">{error}</p>
          <div className="mt-5 flex gap-2 justify-center">
            <button
              onClick={() => window.location.reload()}
              data-testid="microsoft-callback-retry-btn"
              className="h-10 px-5 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:bg-[#F6F7FB]"
            >
              Réessayer
            </button>
            <button
              onClick={() => navigate('/login', { replace: true })}
              data-testid="microsoft-callback-back-btn"
              className="h-10 px-5 rounded-xl ofm-gradient text-white text-[13px] font-semibold"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center" data-testid="microsoft-callback-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--ofm-primary)] mx-auto" />
        <p className="mt-4 text-[14px] text-[#6B6C85]">Finalisation de la connexion Microsoft…</p>
      </div>
    </div>
  );
}
